import { Alert, Button, Modal, Progress, Table, Tag } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { getBankInformationByEmployeeIdApi } from '@nxsys/services/api/employee';
import { verifyBankAccountsBatch } from '@nxsys/services/api/bank';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import {
  BankCheckInput,
  BankCheckOutcome,
  getBatchBankResult,
  normalizeBankDetails,
} from '@nxsys/utils/bulk-bank-verification';

type Result = {
  id: string;
  name: string;
  systemId: string;
  status: BankCheckOutcome['status'] | 'Pending' | 'Skipped' | 'Stopped';
  message: string;
};

type Props = { employees: EmployeeRecordType[]; onClose: () => void };

const BulkBankVerificationModal = ({ employees, onClose }: Props) => {
  const [results, setResults] = useState<Result[]>(() =>
    [
      ...new Map(employees.map((employee) => [employee.id, employee])).values(),
    ].map((employee) => ({
      id: employee.id,
      name: employee.fullName,
      systemId: employee.systemId,
      status: 'Pending',
      message: '',
    })),
  );
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const active = useRef<AbortController | null>(null);
  useEffect(() => () => active.current?.abort(), []);

  const update = (id: string, outcome: Pick<Result, 'status' | 'message'>) => {
    setResults((rows) =>
      rows.map((row) => (row.id === id ? { ...row, ...outcome } : row)),
    );
  };

  const finish = () => {
    setResults((rows) =>
      rows.map((row) =>
        row.status === 'Pending'
          ? {
              ...row,
              status: 'Stopped',
              message:
                'No result recorded. An interrupted request may still consume credits.',
            }
          : row,
      ),
    );
    setRunning(false);
  };

  const run = async () => {
    if (active.current || started) return;
    const controller = new AbortController();
    active.current = controller;
    setStarted(true);
    setRunning(true);
    try {
      // Small sequential batches bound provider load and allow stopping between requests.
      for (
        let offset = 0;
        offset < results.length && !controller.signal.aborted;
        offset += 10
      ) {
        const batch: { id: string; bank: BankCheckInput }[] = [];
        for (const row of results.slice(offset, offset + 10)) {
          if (controller.signal.aborted) break;
          try {
            const response = await getBankInformationByEmployeeIdApi(row.id);
            if (controller.signal.aborted) break;
            if (!response.success || !response.data)
              throw new Error('Unable to load bank details.');
            const bank = normalizeBankDetails({
              accountNumber: response.data.accountNumber ?? '',
              sortCode: response.data.sortCode ?? '',
            });
            if (
              !/^\d{8}$/.test(bank.accountNumber) ||
              !/^\d{6}$/.test(bank.sortCode)
            ) {
              update(row.id, {
                status: 'Skipped',
                message:
                  'Requires an 8-digit account number and 6-digit sort code.',
              });
            } else {
              batch.push({ id: row.id, bank });
            }
          } catch {
            if (!controller.signal.aborted)
              update(row.id, {
                status: 'Error',
                message: 'Unable to load bank details.',
              });
          }
        }
        if (controller.signal.aborted) break;
        if (!batch.length) continue;
        try {
          const items = await verifyBankAccountsBatch(
            batch.map((entry) => entry.bank),
            controller.signal,
          );
          if (controller.signal.aborted) break;
          batch.forEach(({ id, bank }) =>
            update(id, getBatchBankResult(bank, items)),
          );
          if (items.some((item) => item.Error)) break;
        } catch (error) {
          if (!controller.signal.aborted) {
            batch.forEach(({ id }) =>
              update(id, {
                status: 'Error',
                message:
                  error instanceof Error
                    ? error.message
                    : 'Bank verification failed.',
              }),
            );
          }
          break;
        }
      }
    } finally {
      finish();
      active.current = null;
    }
  };

  const complete = results.filter((row) => row.status !== 'Pending').length;
  return (
    <Modal
      open
      title="Verify Bank Details"
      width={850}
      onCancel={onClose}
      closable={!running}
      maskClosable={!running}
      keyboard={!running}
      footer={
        running ? (
          <Button
            onClick={() => {
              active.current?.abort();
              // Bank-detail reads cannot be aborted; let the user close immediately.
              finish();
            }}
          >
            Stop
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Close</Button>
            {!started && (
              <Button type="primary" onClick={run}>
                Verify {results.length} employees
              </Button>
            )}
          </>
        )
      }
    >
      <Alert
        type="info"
        showIcon
        message="Checks saved bank details for the selected employees. Uses verification credits."
        description="Keep this page open. Results are temporary and are not saved to employee profiles. This checks sort codes and account numbers, not account holder names."
      />
      <Progress
        percent={Math.round((complete / Math.max(results.length, 1)) * 100)}
        status={running ? 'active' : 'normal'}
      />
      <Table
        rowKey="id"
        size="small"
        dataSource={results}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 650 }}
        columns={[
          { title: 'Employee ID', dataIndex: 'systemId' },
          { title: 'Employee', dataIndex: 'name' },
          {
            title: 'Status',
            dataIndex: 'status',
            render: (status: Result['status']) => (
              <Tag
                color={
                  status === 'Verified'
                    ? 'green'
                    : status === 'Invalid' || status === 'Error'
                      ? 'red'
                      : 'default'
                }
              >
                {status}
              </Tag>
            ),
          },
          { title: 'Result', dataIndex: 'message' },
        ]}
      />
    </Modal>
  );
};

export default BulkBankVerificationModal;
